import {Component, EventEmitter, Input, Output} from '@angular/core';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {DomainSelector} from '@twpub/core/models/wrappers/domain-selector';
import {DomainTree} from '@twpub/core/models/domain-tree';
import {Domain} from '@twpub/core/models/domain';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {FieldProps} from '@twpub/core/models';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {DomainItemComponent} from '@twpub/shared/components';
import {FormsModule} from '@angular/forms';

/**
 * Displays current domain selection as an indented list with multi-select checkboxes and search.
 */
@Component({
  selector: 'pub-domain-indented-list',
  templateUrl: './domain-indented-list.component.html',
  styleUrls: ['./domain-indented-list.component.scss'],
  standalone: true,
  imports: [DomainItemComponent, FormsModule]
})
export class DomainIndentedListComponent extends BaseConfigurableComponent implements DomainSelector {
  @Input() currentDomainIds?: number[] = []
  @Input() domainTree!: DomainTree

  @Output() selectDomains = new EventEmitter<number[]>()

  domains: Domain[] = []
  filteredDomains: Domain[] = []
  selectedIds: (number | undefined)[] = [];
  allDomain!: Domain;

  allLabel: string = $localize`:@@domain.allSelected:All domains`;
  searchText: string = '';

  /** Maps each displayed domain ID to all original domain IDs that share the same name (case-insensitive) */
  private domainIdAliases = new Map<number, number[]>();

  override doUpdate() {
    super.doUpdate();
    this.allLabel = this.config?.getString('AllLabel') || $localize`:@@domain.allSelected:All domains`;
    this.domainIdAliases = new Map();
    this.domains = this.deduplicateDomains(this.domainTree.topLevelDomains);
    this.selectedIds = this.currentDomainIds?.length ? [...this.currentDomainIds] : [0];
    this.filteredDomains = this.filterDomains();
    this.allDomain = {id: 0, name: this.allLabel, dictionaryId: this.domains[0]?.dictionaryId ?? 0, children: []};
  }

  /**
   * Deduplicates domains by name (case-insensitive) at each level of the tree.
   * Domains with the same name are merged: children are combined and all original IDs are tracked.
   */
  private deduplicateDomains(domains: Domain[]): Domain[] {
    const byName = new Map<string, { domain: Domain; ids: number[] }>();

    for (const domain of domains) {
      const key = domain.name.toLowerCase();
      const existing = byName.get(key);
      if (existing) {
        existing.ids.push(domain.id);
        existing.domain = {
          ...existing.domain,
          children: [...existing.domain.children, ...domain.children]
        };
      } else {
        byName.set(key, {
          domain: {...domain, children: [...domain.children]},
          ids: [domain.id]
        });
      }
    }

    const result: Domain[] = [];
    for (const {domain, ids} of byName.values()) {
      domain.children = this.deduplicateDomains(domain.children);
      this.domainIdAliases.set(domain.id, ids);
      result.push(domain);
    }

    return result;
  }

  /** Resolves a displayed domain ID to all original domain IDs with the same name */
  private resolveAliasIds(id: number): number[] {
    return this.domainIdAliases.get(id) || [id];
  }

  onSelect(domainIds: number[]) {
    const id = domainIds[0];

    if (id === 0) {
      this.selectDomains.emit([]);
      return;
    }

    const currentIds = (this.currentDomainIds?.length ? [...this.currentDomainIds] : []).filter(i => i !== 0);

    const domain = this.findDomainInDeduped(this.domains, id);
    const displayIdsToToggle = domain ? [id, ...this.collectDescendantIds(domain)] : [id];

    // Resolve each display ID to all alias IDs so search covers all dictionaries
    const idsToToggle = displayIdsToToggle.flatMap(did => this.resolveAliasIds(did));

    const isAdding = !this.isSelected(id);
    let updatedIds: number[];

    if (isAdding) {
      updatedIds = [...new Set([...currentIds, ...idsToToggle])];
    } else {
      const removeSet = new Set(idsToToggle);
      if (domain?.parentId) {
        const parentAliases = this.resolveAliasIds(domain.parentId);
        parentAliases.forEach(pid => removeSet.add(pid));
      }
      updatedIds = currentIds.filter(existingId => !removeSet.has(existingId));
    }

    this.selectDomains.emit(updatedIds);
  }

  private findDomainInDeduped(domains: Domain[], id: number): Domain | undefined {
    for (const domain of domains) {
      if (domain.id === id) return domain;
      const found = this.findDomainInDeduped(domain.children, id);
      if (found) return found;
    }
    return undefined;
  }

  collectDescendantIds(domain: Domain): number[] {
    const ids: number[] = [];
    for (const child of domain.children || []) {
      ids.push(child.id);
      ids.push(...this.collectDescendantIds(child));
    }
    return ids;
  }

  isSelected = (id: number | undefined) => {
    if (id === undefined) return false;
    if (id === 0) return this.selectedIds.includes(0);
    // Check if any alias of this domain is in the current selection
    const aliases = this.resolveAliasIds(id);
    return aliases.some(aid => this.selectedIds.includes(aid));
  }

  isExpanded(domain: Domain): boolean {
    return this.isSelected(domain.id) || domain.children?.some(child => this.isSelected(child.id)) || false;
  }

  filterDomains(): Domain[] {
    if (!this.searchText?.trim()) {
      return this.domains;
    }
    const query = this.searchText.trim().toLowerCase();
    const result: Domain[] = [];
    for (const domain of this.domains) {
      if (domain.name.toLowerCase().includes(query)) {
        result.push(domain);
      } else if (domain.children?.length) {
        const matchingChildren = domain.children.filter(child =>
          child.name.toLowerCase().includes(query)
        );
        if (matchingChildren.length) {
          result.push({...domain, children: matchingChildren});
        }
      }
    }
    return result;
  }

  onSearchChange() {
    this.filteredDomains = this.filterDomains();
  }

  static readonly compName = 'DomainIndentedListComponent';
  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [this.LABEL_FIELD, this.VISIBLE_FIELD,
      {
        key: 'AllLabel',
        label: '"All domains" label',
        controlType: ControlType.Textbox,
        description: 'Label for the "All domains" option'
      }
    ];

    constructor() {
      super(DomainIndentedListComponent, DomainIndentedListComponent.compName, 'Indented List', [ComponentCategory.DomainSelector]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<DomainSelector>(DomainIndentedListComponent, this.name, initialConfig);
    }
  }
}
